import { api } from './client';
import { readSalesOrgs } from '@features/settings/users/model/salesOrgs';
import type { ApiResponse, EntityId } from './types';
import type {
  ChangePasswordLoggedInRequest,
  ChangePasswordRequest,
  ForgotPasswordRequest,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  User,
  UserInfo,
  ValidateOTPRequest,
  ValidateOTPResponse,
} from '@core/types/auth';

/**
 * HTTP half of `core/services/auth.service.ts`.
 *
 * Deliberately contains NO navigation, NO toasts, and NO storage writes. The Angular
 * service did all three from inside its `login()` tap, wrapped in
 * `setTimeout(..., 500)` because the snackbar and `router.navigateByUrl` raced each
 * other. Splitting them lets the login page own the sequence and deletes the timeout.
 *
 * `skipAuthRedirect` on the login/forgot/OTP calls stops the response interceptor
 * treating a 401 as an expired session — on those endpoints a 401 means bad
 * credentials and the server's own message must reach the user.
 */
export const authApi = {
  register: (request: RegisterRequest) =>
    api
      .post<ApiResponse<User>>('/users/register', request, {
        meta: { fallbackMessage: 'Registration failed' },
      })
      .then((response) => response.data),

  login: (credentials: LoginRequest) =>
    api
      .post<LoginResponse>('/users/login', credentials, {
        meta: { skipAuthRedirect: true, fallbackMessage: 'Login failed' },
      })
      .then((response) => response.data),

  /** Server-side session teardown. Storage clearing is the store's job. */
  logout: () =>
    api
      .post<ApiResponse<null>>('/users/logout', {}, { meta: { silent: true } })
      .then((response) => response.data),

  forgotPassword: (request: ForgotPasswordRequest) =>
    api
      .post<ApiResponse<null>>('/users/forgot-password', request, {
        meta: { skipAuthRedirect: true, fallbackMessage: 'Failed to send OTP' },
      })
      .then((response) => response.data),

  validateOTP: (request: ValidateOTPRequest) =>
    api
      .post<ValidateOTPResponse>('/users/validate-otp', request, {
        meta: { skipAuthRedirect: true, fallbackMessage: 'Failed to verify OTP' },
      })
      .then((response) => response.data),

  /** OTP-scoped reset. Uses the token returned by `validateOTP`. */
  changePasswordWithOtp: (request: ChangePasswordRequest) =>
    api
      .post<ApiResponse<null>>('/users/change-password-otp', request, {
        meta: { skipAuthRedirect: true, fallbackMessage: 'Failed to change password' },
      })
      .then((response) => response.data),

  /** Signed-in password change (My Profile). */
  changePassword: (request: ChangePasswordLoggedInRequest) =>
    api
      .post<ApiResponse<null>>('/users/change-password', request, {
        meta: { fallbackMessage: 'Failed to change password' },
      })
      .then((response) => response.data),

  userInfo: () =>
    api
      .get<ApiResponse<UserInfo>>('/management/userinfo', {
        meta: { fallbackMessage: 'Failed to load profile' },
      })
      .then((response) => response.data),

  /**
   * The full user list. Named `allUserSInfo` in the Angular service (typo included);
   * renamed here because nothing depends on the old spelling.
   */
  /**
   * Every user, or just the holders of one role.
   *
   * `role_name` narrows the list server-side, which is what the Company Setup assign picker wants: the
   * candidates for "who can approve as CEO here" are the people who hold CEO, not the whole directory.
   *
   * ⚠️ THE PARAMETER IS NOT IN THE PUBLISHED SPEC. `/management/usersinfo` is documented with no
   * parameters at all, but the running backend accepts `role_name` (confirmed by the curl this was
   * built from). Sending it against a backend that predates it is harmless rather than a 422 — FastAPI
   * ignores undeclared QUERY parameters, unlike a request body, so an older instance simply answers with
   * every user and the picker degrades to its previous behaviour.
   *
   * Omitted rather than sent empty when absent: `role_name=` would filter for the role whose name is the
   * empty string.
   *
   * ⚠️ AN UNKNOWN ROLE IS A 404, NOT AN EMPTY LIST — `{"message": "Role not found for role_name: …"}`,
   * confirmed against the running backend. That is why `silent` exists here: the assign picker renders
   * its own inline message, and a toast on top of an open dialog says the same thing twice. The Manage
   * Users list keeps the toast, because there a failure has nothing else to show.
   */
  allUsersInfo: (filters: { roleName?: string; search?: string; silent?: boolean } = {}) =>
    api
      .get<ApiResponse<unknown[]>>('/management/usersinfo', {
        params: {
          ...(filters.roleName ? { role_name: filters.roleName } : {}),
          ...(filters.search?.trim() ? { search: filters.search.trim() } : {}),
        },
        meta: { fallbackMessage: 'Failed to load users', silent: filters.silent },
      })
      .then((response) => response.data),

  getUserById: (userId: EntityId) =>
    api
      .get<ApiResponse<unknown>>(`/management/${userId}`, {
        meta: { fallbackMessage: 'Failed to load user' },
      })
      .then((response) => response.data),

  /**
   * Admin-created user. `POST /management/create`, from `ProfileService.createUser`.
   *
   * NOT `/users/register` — that is self-service sign-up, which takes no roles. Using it here would
   * create a user with no permissions and no error.
   *
   * `roles_assigned` carries UUID strings; the Angular type declared `number[]`, as it did for every
   * other id in this API.
   */
  createUser: (payload: {
    first_name: string;
    last_name: string;
    email: string;
    password: string;
    roles_assigned: EntityId[];
    /**
     * Company UUIDs — `company_ids` on the `UserCreate` schema, and the reason the create screen has a
     * company-code picker.
     *
     * ⚠️ IDS, NOT CODES. `"1000"` is a code; the API wants `"01a013d0-c22b-7f57-a7f1-389bf85169aa"`.
     * `/dropdown/company_code` carries both — `/approval-matrix/company-codes` answers `{code, value}`
     * with no id at all, so it cannot feed this.
     *
     * Optional on the schema, so it is omitted rather than sent empty when nothing is picked.
     */
    company_ids?: EntityId[];
    /**
     * Sales-organization UUIDs, from `POST /management/sales-orgs/by-company-codes`.
     *
     * ⚠️ WHICH ORGS EXIST DEPENDS ON THE COMPANY CODES PICKED ABOVE, so this cannot be chosen first
     * and the screen asks for it second.
     *
     * ⚠️ AND `UserCreate` DOES NOT REJECT UNKNOWN KEYS. It is declared with `additionalProperties`
     * unset, so Pydantic ignores anything it does not recognise: a misspelling here is dropped in
     * silence and answered with 201, and the user is created with no sales organization at all.
     * The name is `sales_org_ids`, confirmed against the published schema.
     *
     * Optional on the schema, so it is omitted rather than sent empty when nothing is picked.
     */
    sales_org_ids?: EntityId[];
  }) =>
    api
      .post<ApiResponse<unknown>>('/management/create', payload, {
        meta: { fallbackMessage: 'Failed to create user' },
      })
      .then((response) => response.data),

  /**
   * The sales organizations mapped to a set of company codes.
   *
   * ⚠️ CODES, NOT IDS — the opposite of `company_ids` on the create payload. The picker holds company
   * UUIDs because that is what the create call wants, so the caller has to translate back through
   * the dropdown options before asking this. Sending UUIDs here matches nothing and answers 200 with
   * an empty list, which reads on screen as "this company has no sales organizations".
   *
   * ⚠️ NEVER CALL IT WITH AN EMPTY LIST. `company_codes` is declared `minItems: 1`, so an empty
   * array is a 422 and a toast, not an empty result.
   *
   * The response is declared untyped (`200: {schema: {}}`) and is read by `readSalesOrgs` rather than
   * by the shared dropdown normaliser — which has no `<something>_code` sweep and so mistakes
   * `sales_org_id` for the code. See the note there.
   */
  salesOrgsByCompanyCodes: (companyCodes: readonly string[]) =>
    api
      .post<unknown>(
        '/management/sales-orgs/by-company-codes',
        { company_codes: companyCodes },
        { meta: { fallbackMessage: 'Failed to load sales organizations' } },
      )
      .then((response) => readSalesOrgs(response.data)),

  /* ------------------------------------------ a user's sales organizations ---- */

  /**
   * Adds sales organizations to a user. Additive — it does not replace the list.
   *
   * ⚠️ THERE IS NO "SET THE WHOLE LIST" CALL, which is why the dialog diffs. Sending every selected
   * org on each save would re-assign ones the user already had, and hide which change actually
   * failed behind a pile of pointless requests.
   */
  assignUserSalesOrgs: (userId: EntityId, salesOrgIds: readonly EntityId[]) =>
    api
      .post<ApiResponse<unknown>>(
        '/management/user-sales-orgs',
        { user_id: userId, sales_org_ids: salesOrgIds },
        { meta: { fallbackMessage: 'Failed to assign sales organizations' } },
      )
      .then((response) => response.data),

  /** Removes ONE, by path — the other half of the diff. */
  removeUserSalesOrg: (userId: EntityId, salesOrgId: EntityId) =>
    api
      .delete<ApiResponse<unknown>>(
        `/management/user-sales-orgs/${String(userId)}/${String(salesOrgId)}`,
        { meta: { fallbackMessage: 'Failed to remove the sales organization' } },
      )
      .then((response) => response.data),

  toggleUserActivation: (userId: EntityId, isActive: boolean) =>
    api
      .post<ApiResponse<null>>(
        '/management/activation',
        { user_id: userId, is_active: isActive },
        { meta: { fallbackMessage: 'Failed to update user status' } },
      )
      .then((response) => response.data),

  updateUserRoles: (userId: EntityId, roleIds: EntityId[]) =>
    api
      .put<ApiResponse<null>>(
        '/management/update',
        { user_id: userId, role_ids: roleIds },
        { meta: { fallbackMessage: 'Failed to update roles' } },
      )
      .then((response) => response.data),

  /**
   * ============================================================================
   * A user's company codes, on an EXISTING user
   * ============================================================================
   *
   * ⚠️ THERE IS NO "SET THE WHOLE LIST" CALL, which shapes how the edit dialog saves. Adding and
   * removing are separate endpoints with different shapes, so the dialog diffs the selection and issues
   * only what changed — see `diffCompanyIds`.
   *
   * `POST /management/user-companies` is ONE COMPANY TO MANY USERS (`{company_id, user_ids}`), the
   * opposite of what this screen needs, so adding N companies to one user is N calls. Passing a single
   * user id is the intended narrow case, not a workaround.
   *
   * Company creation takes `company_ids` in one go — that is `POST /management/create` only.
   */
  assignUserCompany: (companyId: EntityId, userIds: EntityId[]) =>
    api
      .post<ApiResponse<unknown>>(
        '/management/user-companies',
        { company_id: companyId, user_ids: userIds },
        { meta: { fallbackMessage: 'Failed to assign the company code' } },
      )
      .then((response) => response.data),

  /**
   * The companies a user is EXPLICITLY mapped to — the rows `removeUserCompany` can delete.
   *
   * ⚠️ NOT THE SAME AS `usersinfo`'s `companies`. For a user whose role is global (Super Admin, Global
   * Company Admin) `usersinfo` lists every company code, while this returns none: their access comes
   * from the role, not from mappings. Editing from the `usersinfo` list offered all 32 codes ticked,
   * and un-ticking one called the DELETE below for a mapping that never existed — "User-company
   * mapping not found". Verified live on root@vendor.com and global.admin@vendor.com.
   */
  userCompanyIds: (userId: EntityId) =>
    api
      .get<ApiResponse<{ companies?: { company_id?: string | null }[] | null }>>(
        `/management/user-companies/${encodeURIComponent(String(userId))}`,
        { meta: { fallbackMessage: "Failed to load the user's company codes" } },
      )
      .then((response) =>
        (response.data.data?.companies ?? [])
          .map((company) => String(company.company_id ?? '').trim())
          .filter(Boolean),
      ),

  removeUserCompany: (userId: EntityId, companyId: EntityId) =>
    api
      .delete<ApiResponse<unknown>>(
        `/management/user-companies/${encodeURIComponent(String(userId))}/${encodeURIComponent(String(companyId))}`,
        { meta: { fallbackMessage: 'Failed to remove the company code' } },
      )
      .then((response) => response.data),

  resetUserPassword: (userId: EntityId, newPassword: string, confirmPassword: string) =>
    api
      .put<ApiResponse<null>>(
        `/management/reset-password/${userId}`,
        { new_password: newPassword, confirm_password: confirmPassword },
        { meta: { fallbackMessage: 'Failed to reset password' } },
      )
      .then((response) => response.data),
};
